import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { UserService } from '../services/user.service';
import { DialogsService } from '../services/dialogs/dialogs.service';

/**
 * Clears the stored client session when the API rejects its token.
 *
 * The app restores login state from localStorage without validating the
 * token against the server, so a token invalidated server-side (e.g. the
 * signing key rotation) leaves the user "half logged in": the UI shows
 * them as logged in while every authenticated request fails with a 401.
 * This interceptor detects that state, clears the dead session, and lets
 * the user log in again.
 *
 * Dependencies are resolved lazily through Injector because UserService
 * (via SalonmonsterHttpClient) and TranslateService both depend on
 * HttpClient, and injecting them in the constructor would create a
 * circular dependency with HTTP_INTERCEPTORS.
 */
@Injectable()
export class SessionExpiredInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: any) => {
        if (this.isExpiredSessionError(req, error)) {
          this.forceLogout();
        }
        return throwError(error);
      })
    );
  }

  private isExpiredSessionError(req: HttpRequest<any>, error: any): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
      return false;
    }

    // Only react to our own API, not third-party requests.
    if (!req.url.startsWith(environment.API_ROOT)) {
      return false;
    }

    // Only a 401 received *while logged in* means the stored token is dead.
    // Failed login attempts happen while logged out and must not trigger this.
    const userService = this.injector.get(UserService);
    return userService.isLoggedIn();
  }

  private forceLogout(): void {
    const userService = this.injector.get(UserService);
    userService.clearSession();

    try {
      const translateService = this.injector.get(TranslateService);
      const dialogsService = this.injector.get(DialogsService);
      const router = this.injector.get(Router);

      dialogsService
        .alert(
          translateService.instant('AUTH.SESSION_EXPIRED_TITLE'),
          translateService.instant('AUTH.SESSION_EXPIRED')
        )
        .subscribe(() => {
          // The page the user was on just failed to load with the dead
          // token, so send them somewhere that renders cleanly while
          // logged out rather than leaving that error state on screen.
          router.navigate(['/']);
        });
    } catch (e) {
      // Never let the notification break the logout itself.
    }
  }
}
